import { useCallback, useEffect, useLayoutEffect, useState } from "react";

/**
 * Hook for positioning a dropdown relative to a trigger button.
 * Handles scroll updates and viewport-aware flipping (above/below).
 *
 * Call updatePosition() explicitly before opening the dropdown so the
 * initial position is calculated from the current button rect.
 * useLayoutEffect then flips the menu above the button if it would overflow
 * the bottom of the viewport — this runs synchronously before the browser
 * paints, so there is no visible flash.
 *
 * @param {React.RefObject} buttonRef  - ref on the trigger button
 * @param {boolean} isOpen
 * @param {Object} options - { offsetX, offsetY, align: 'left'|'right', menuWidth, menuRef }
 * @returns {{ position: { top: number, left: number }, updatePosition: () => void }}
 */
export const useDropdownPosition = (
  buttonRef,
  isOpen,
  options = {}
) => {
  const { offsetX = 0, offsetY = 4, align = "left", menuWidth = 200, menuRef } = options;

  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!buttonRef?.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const left =
      align === "right"
        ? rect.right - menuWidth + offsetX
        : rect.left + offsetX;
    setPosition({
      top: rect.bottom + offsetY,
      left,
    });
  }, [buttonRef, align, offsetX, offsetY, menuWidth]);

  // Keep the rendered menu inside the viewport: flip above the button when
  // it would overflow the bottom, and slide it left/right when it would
  // overflow either side (a trigger near the right edge — e.g. the last
  // column of a calendar grid — would otherwise push a left-aligned menu
  // straight off-screen). useLayoutEffect fires synchronously after DOM
  // mutations but before paint, so the correction is invisible to the user.
  useLayoutEffect(() => {
    if (!isOpen || !menuRef?.current || !buttonRef?.current) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportMargin = 8;

    setPosition((previous) => {
      let { top, left } = previous;

      if (menuRect.bottom > window.innerHeight) {
        top = buttonRect.top - menuRect.height - offsetY;
      }

      if (menuRect.right > window.innerWidth - viewportMargin) {
        left = window.innerWidth - menuRect.width - viewportMargin;
      } else if (menuRect.left < viewportMargin) {
        left = viewportMargin;
      }

      return { top, left };
    });
  }, [isOpen, menuRef, buttonRef, offsetY]);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => updatePosition();
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [isOpen, updatePosition]);

  return { position, updatePosition };
};
